import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleFilled, ClockCircleFilled } from '@ant-design/icons';
import styled from 'styled-components';

import { IBuilderSaveResult } from 'components/EmbeddedBuilder';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { tint, wz, wzType } from '../../wizard/wizardStyles';
import { savedRecordRoute } from '../configSubmission';
import { TC } from './ConfigPanel';

// Not drawn (no result screen exists for any flow); the campaign wizard's result card, at 950.
const Banner = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 950px;
  margin: 0 auto;
  padding: 40px 30px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 16px;
  background: ${wz.panel};
  text-align: center;
`;

const Icon = styled.span<{ $pending: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: ${({ $pending }) => tint($pending ? wz.highlight : wz.success, 0.14)};
  color: ${({ $pending }) => ($pending ? wz.highlight : wz.success)};
  font-size: 22px;
`;

const Title = styled.h1`
  margin: 14px 0 0;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.title}
`;

const Body = styled.p`
  margin: 6px 0 0;
  color: ${wz.secondary};
  ${wzType.body}
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;

  > * + * {
    margin-left: 12px;
  }
`;

const ActionLink = styled(Link)`
  color: ${wz.accent};
  font-weight: 700;
  ${wzType.input}
`;

const Again = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: ${wz.secondary};
  font-weight: 700;
  cursor: pointer;
  ${wzType.input}
`;

interface IConfigResultViewProps {
  /** "Rule" / "Reward" / "Audience". */
  entity: string;
  name: string;
  result: IBuilderSaveResult;
  onCreateAnother: () => void;
}

/** After a save: the record, or its approval request under maker-checker. */
const ConfigResultView: React.FC<IConfigResultViewProps> = ({
  entity,
  name,
  result,
  onCreateAnother
}) => {
  const { t } = useMultiLanguageTranslation();
  const pending = (result.pendingRequestId ?? null) !== null;
  const title = pending
    ? t(`${TC}.result.pending`)
    : result.savedAsDraft
    ? t(`${TC}.result.draft`)
    : t(`${TC}.result.confirmed`, { entity });
  return (
    <Banner data-testid="orbit-config-result" role="status">
      <Icon $pending={pending} aria-hidden="true">
        {pending ? <ClockCircleFilled /> : <CheckCircleFilled />}
      </Icon>
      <Title>{title}</Title>
      <Body>{`${name} (id: ${result.id})`}</Body>
      <Actions>
        <ActionLink to={savedRecordRoute(result)}>
          {pending
            ? t(`${TC}.result.viewRequest`)
            : t(`${TC}.result.view`, { entity: entity.toLowerCase() })}
        </ActionLink>
        <Again type="button" onClick={onCreateAnother}>
          {t(`${TC}.result.another`)}
        </Again>
      </Actions>
    </Banner>
  );
};

export default ConfigResultView;
